import { useEffect, useRef, useState, type ReactNode } from 'react';

import { ResearchTourTooltip } from './ResearchTourTooltip';
import { createResearchTutorialActions } from './researchTutorialActions';
import {
  ResearchTutorialContext,
  useResearchTutorialActions,
  useResearchTutorialStore,
  useResearchTutorialStoreApi,
} from './researchTutorialContext';

import { SpotlightTourProvider, useSpotlightTour } from '@/components/spotlight/SpotlightTour';
import { View } from '@/components/ui/view';
import { DURATION } from '@/constants/animation';
import { RESEARCH_TUTORIAL_SEGMENTS } from '@/constants/researchTutorial';
import { useColors } from '@/hooks/useColors';
import { useTourCallout } from '@/hooks/useTourCallout';
import {
  createResearchTutorialStore,
  selectReadySegment,
} from '@/services/stores/researchTutorialStore';
import { CALLOUT_KEYS } from '@/services/userCallouts';

/**
 * How long a segment waits after its first target mounts before it shows.
 * Long enough for the screen's push transition and the composer's terms reveal
 * to settle.
 */
const START_DELAY = DURATION.slow + DURATION.fast;

/**
 * Shows the segment the tutorial is waiting on once its first target has
 * mounted, and takes a segment off the screen when the tutorial moves on
 * without it or its first target goes away, as it does when the reader opens
 * another conversation. An interrupted segment starts again from its first
 * step when its target comes back.
 */
function ResearchTutorialDriver() {
  const store = useResearchTutorialStoreApi();

  const { segmentShown } = useResearchTutorialActions();

  const phase = useResearchTutorialStore((state) => state.phase);

  const running = useResearchTutorialStore((state) => state.running);

  const readySegment = useResearchTutorialStore(selectReadySegment);

  const example = useSpotlightTour(RESEARCH_TUTORIAL_SEGMENTS.example.tourKey);

  const compose = useSpotlightTour(RESEARCH_TUTORIAL_SEGMENTS.compose.tourKey);

  const results = useSpotlightTour(RESEARCH_TUTORIAL_SEGMENTS.results.tourKey);

  const controllersRef = useRef({ example, compose, results });

  useEffect(() => {
    controllersRef.current = { example, compose, results };
  });

  useEffect(() => {
    if (running && (running !== phase || running !== readySegment)) {
      controllersRef.current[running].stop();

      store.getState().segmentInterrupted(running);
    }
  }, [running, phase, readySegment, store]);

  useEffect(() => {
    if (!readySegment || running === readySegment) {
      return;
    }

    const timer = setTimeout(() => {
      if (controllersRef.current[readySegment].start()) {
        segmentShown(readySegment);
      }
    }, START_DELAY);

    return () => clearTimeout(timer);
  }, [readySegment, running, segmentShown]);

  return null;
}

/**
 * Hosts the Research tutorial for every Ask screen beneath it. Lives in the Ask
 * layout rather than the screen because the first send replaces the
 * new-conversation route with the saved one, which remounts the screen, and the
 * tour has to carry on into the results.
 *
 * The spotlight dims the whole window, the native header included. Its card
 * may render outside this tree, so it carries the tutorial's context with it.
 *
 * Dark mode dims with black at a heavier alpha. The light theme's scrim is
 * itself a near-black, and over an already dark screen it barely darkens, so
 * the highlighted target stops standing out from what surrounds it.
 */
export function ResearchTutorialProvider({ children }: { children: ReactNode }) {
  const { colors, isDarkScheme } = useColors();

  const callout = useTourCallout({ key: CALLOUT_KEYS.RESEARCH_TUTORIAL_INTRO, enabled: true });

  const calloutRef = useRef(callout);

  const [tutorial] = useState(() => {
    const store = createResearchTutorialStore();

    return { store, ...createResearchTutorialActions(store, calloutRef) };
  });

  const [contextValue] = useState(() => ({ store: tutorial.store, actions: tutorial.actions }));

  useEffect(() => {
    calloutRef.current = callout;
  });

  useEffect(() => {
    if (callout.shouldShow) {
      tutorial.retryStart();
    }
  }, [callout.shouldShow, tutorial]);

  return (
    <View className="flex-1">
      <ResearchTutorialContext.Provider value={contextValue}>
        <SpotlightTourProvider
          renderCard={(step) => (
            <ResearchTutorialContext.Provider value={contextValue}>
              <ResearchTourTooltip step={step} />
            </ResearchTutorialContext.Provider>
          )}
          backdropColor={isDarkScheme ? `${colors.black}CC` : `${colors.background.dark}99`}>
          <ResearchTutorialDriver />

          {children}
        </SpotlightTourProvider>
      </ResearchTutorialContext.Provider>
    </View>
  );
}
